import { useEffect, useRef, useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import {
  authClient,
  clearStashedRole,
  companyInviteTokenFromPath,
  peekStashedRole,
  roleFromPath,
  roleHome,
  roleSetupPath,
  navigateSafeReturn,
  safeReturnPath,
  takeReturnTo,
  type IntendedRole,
  employerPersonalEmailBlocked,
  signOutIfEmployerPersonalEmail,
  signOutSucceeded,
  EMPLOYER_PERSONAL_EMAIL_MESSAGE,
} from "@/lib/auth-client"
import { Skeleton } from "@/components/ui/skeleton"
import { needsCandidateOnboarding } from "@/lib/candidate"
import { errorMessage } from "@/lib/errors"
import { toast } from "sonner"
import { useCompanyWorkspaceState } from "@/features/company"

const OTT_PENDING_KEY = "hsr_ott_pending"

export const Route = createFileRoute("/onboarding/role")({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => ({
    returnTo: typeof search.returnTo === "string" ? search.returnTo : undefined,
  }),
  component: RoleOnboardingBridge,
})

/**
 * Silent post-auth bridge — not a role picker when the role is known.
 * Role is chosen at `/jobs/join` or `/recruit/join`. This route:
 * - sends existing profiles to their desk (or returnTo)
 * - applies a stashed/inferred join role and continues to that setup screen
 * - otherwise shows Recruiter / Candidate links straight to those signup pages
 */
function RoleOnboardingBridge() {
  const { returnTo: returnToSearch } = Route.useSearch()
  const profile = useQuery(api.profiles.me)
  const workspace = useCompanyWorkspaceState()
  const ensureProfile = useMutation(api.profiles.ensureProfile)
  const { data: session, isPending } = authClient.useSession()
  const navigate = useNavigate()
  const [stashedReturn] = useState(() => takeReturnTo())
  const ranRef = useRef(false)
  const [needsPick, setNeedsPick] = useState(false)

  const [ottWait, setOttWait] = useState(() => {
    if (typeof window === "undefined") return false
    if (new URLSearchParams(window.location.search).has("ott")) {
      sessionStorage.setItem(OTT_PENDING_KEY, "1")
      return true
    }
    return sessionStorage.getItem(OTT_PENDING_KEY) === "1"
  })

  const resolvedReturn =
    safeReturnPath(returnToSearch) ?? safeReturnPath(stashedReturn)

  const destFor = (r: IntendedRole) => resolvedReturn ?? roleHome(r)

  useEffect(() => {
    if (session?.user) {
      sessionStorage.removeItem(OTT_PENDING_KEY)
      setOttWait(false)
      return
    }
    if (!ottWait) return
    const t = window.setTimeout(() => {
      sessionStorage.removeItem(OTT_PENDING_KEY)
      setOttWait(false)
    }, 2500)
    return () => window.clearTimeout(t)
  }, [session, ottWait])

  useEffect(() => {
    if (isPending || ottWait || profile === undefined || ranRef.current) return

    if (profile && employerPersonalEmailBlocked(profile)) {
      ranRef.current = true
      void signOutSucceeded().then(() => {
        toast.error(EMPLOYER_PERSONAL_EMAIL_MESSAGE)
        void navigate({ to: "/recruit/join" })
      })
      return
    }

    if (profile?.role === "employer" && workspace === undefined) return

    if (!session?.user) {
      ranRef.current = true
      void navigate({
        to: "/login",
        search: {
          returnTo: resolvedReturn ?? "/onboarding/role",
        },
      })
      return
    }

    if (profile) {
      ranRef.current = true
      if (profile.role === "employer" && workspace?.state === "no_company") {
        void navigate({
          to: "/onboarding/company",
          search: resolvedReturn ? { returnTo: resolvedReturn } : {},
        })
        return
      }
      if (profile.role === "seeker" && needsCandidateOnboarding(profile)) {
        void navigate({
          to: "/onboarding/candidate",
          search: resolvedReturn ? { returnTo: resolvedReturn } : {},
        })
        return
      }
      navigateSafeReturn(navigate, destFor(profile.role), roleHome(profile.role))
      return
    }

    const stashed =
      peekStashedRole() ??
      roleFromPath(returnToSearch) ??
      roleFromPath(stashedReturn)
    if (!stashed) {
      ranRef.current = true
      setNeedsPick(true)
      return
    }

    ranRef.current = true
    // Employer profile creation requires a verified work inbox — detour to
    // the OTP step instead of letting ensureProfile reject.
    if (stashed === "employer" && session.user.emailVerified === false) {
      void navigate({
        to: "/recruit/verify",
        search: resolvedReturn ? { returnTo: resolvedReturn } : {},
      })
      return
    }
    void (async () => {
      try {
        const { claimedCompany } = await ensureProfile({
          role: stashed,
          name: session.user.name || undefined,
          inviteToken:
            stashed === "employer"
              ? companyInviteTokenFromPath(resolvedReturn) ?? undefined
              : undefined,
        })
        clearStashedRole()
        if (claimedCompany) {
          void navigate({ to: roleHome(stashed) })
          return
        }
        void navigate({
          to: roleSetupPath(stashed),
          search: resolvedReturn ? { returnTo: resolvedReturn } : {},
        })
      } catch (e) {
        ranRef.current = false
        if (await signOutIfEmployerPersonalEmail(e)) {
          toast.error(errorMessage(e, "Failed to finish signup"))
          void navigate({ to: "/recruit/join" })
          return
        }
        toast.error(errorMessage(e, "Failed to finish signup"))
        setNeedsPick(true)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, ottWait, session, profile, workspace, navigate, returnToSearch])

  if (needsPick) {
    return (
      <div className="mkt-auth home-wf">
        <div className="mkt-auth-card">
          <div className="mkt-auth-panel">
            <header className="mkt-auth-head">
              <h1 className="mkt-auth-title">Finish signing up</h1>
              <p className="mkt-auth-desc">
                Go to Recruiter or Candidate — we'll take you to that signup.
              </p>
            </header>
            <div className="mkt-auth-actions">
              <Link to="/recruit/join" className="mkt-auth-google">
                Go to Recruiter
              </Link>
              <Link to="/jobs/join" className="mkt-auth-submit">
                Go to Candidate
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mkt-page home-wf">
      <div className="mkt-page-shell mkt-page-shell--narrow">
        <Skeleton className="h-40 w-full" />
      </div>
    </div>
  )
}
